import React, { useEffect, useRef, useState } from 'react';
import styled, { FlattenSimpleInterpolation } from 'styled-components';
import tw from 'tailwind.macro';
import { NAVIGATION } from '../../containers/App/constants';
import { useApp } from '../../contexts/App';
import { ISearchCity, ISearchContext } from '../../contexts/Search/types';
import { isMobileApp } from '../../helpers/app';
import windowSize from '../../helpers/windowSize';
import { useDelayedAction } from '../../hooks/useDelayedAction';
import { dayjs } from '../../libs/day';
import history from '../../utils/history';
import { createStay22Url } from '../../utils/stay22';
import { FOCUS_NEXT_INPUT_DELAY } from './constants';
import SearchBar from './SearchBar';
import SearchPanel, { ESearchPanel } from './SearchPanel';
import { IHoveredCitiesIndex, IPopularCities, ISuggestedCities } from './types';
import { validatePassengersAge } from './utils';

const Container = styled.div`
  ${tw`flex justify-center`}
  gap:var(--size-huge);
`;

export interface TravelSearchBarProps {
  panelStyle?: FlattenSimpleInterpolation;
  searchContext: ISearchContext;
  onPanelChange?: (isOpen: boolean, panel: ESearchPanel) => void;
}

/**
 * @description Returns a travel search component
 * @param {FlattenSimpleInterpolation} props.panelStyle Allows you to override the panel's css style
 * @param {ISearchContext} props.searchContext Context for modifying search parameters
 * @param {(isOpen: boolean, panel: ESearchPanel) => void} props.onPanelChange Function that can be called up when opening and closing panels
 * @returns {React.FC<TravelSearchBarProps>}
 */
const TravelSearchBar: React.FC<TravelSearchBarProps> = ({
  onPanelChange,
  panelStyle,
  searchContext,
}) => {
  const { partner, referer } = useApp();
  const partnerId = partner?.gpuids ? partner.id : undefined;

  const { cancel, delay } = useDelayedAction();

  const formRef = useRef<HTMLFormElement>(null);
  const passengersFormRef = useRef<HTMLFormElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const closePanelButtonRef = useRef<HTMLButtonElement>(null);

  const originCityRef = useRef<HTMLInputElement>(null);
  const destinationCityRef = useRef<HTMLInputElement>(null);
  const returnDateRef = useRef<HTMLInputElement>(null);
  const passengersRef = useRef<HTMLInputElement>(null);
  const departureDateRef = useRef<HTMLInputElement>(null);

  const [displayedPanel, setDisplayedPanel] = useState<ESearchPanel | null>(
    null,
  );

  const [popularCities, setPopulaCities] = useState<IPopularCities>({
    [ESearchPanel.ORIGIN_CITY]: null,
    [ESearchPanel.DESTINATION_CITY]: null,
  });

  const [suggestedCities, setSuggestedCities] = useState<ISuggestedCities>({
    [ESearchPanel.ORIGIN_CITY]: null,
    [ESearchPanel.DESTINATION_CITY]: null,
  });

  const [hoveredCitiesIndex, sethoveredCitiesIndex] =
    useState<IHoveredCitiesIndex>({
      [ESearchPanel.DESTINATION_CITY]: 0,
      [ESearchPanel.ORIGIN_CITY]: 0,
    });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closePanelButtonRef?.current?.click();
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  function onIncrementHoveredCity(
    type: ESearchPanel.ORIGIN_CITY | ESearchPanel.DESTINATION_CITY,
  ) {
    let index: number;

    if (
      suggestedCities[type] !== null &&
      hoveredCitiesIndex[type] >= suggestedCities[type].length - 1
    ) {
      index = 0;
    } else if (
      popularCities[type] !== null &&
      hoveredCitiesIndex[type] >= popularCities[type].length - 1
    ) {
      index = 0;
    } else {
      index = hoveredCitiesIndex[type] + 1;
    }

    sethoveredCitiesIndex((hoveredCitiesIndex) => ({
      ...hoveredCitiesIndex,
      [type]: index,
    }));
  }

  function onDecrementHoveredCity(
    type: ESearchPanel.ORIGIN_CITY | ESearchPanel.DESTINATION_CITY,
  ) {
    let index = hoveredCitiesIndex[type] - 1;

    if (hoveredCitiesIndex[type] === 0) {
      if (suggestedCities[type] !== null) {
        index = suggestedCities[type].length - 1;
      } else if (popularCities[type] !== null) {
        index = popularCities[type].length - 1;
      }
    }

    sethoveredCitiesIndex((hoveredCitiesIndex) => ({
      ...hoveredCitiesIndex,
      [type]: index,
    }));
  }

  function onCitiesInputKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
    type: ESearchPanel.ORIGIN_CITY | ESearchPanel.DESTINATION_CITY,
  ) {
    if (event.key === 'ArrowUp') {
      onDecrementHoveredCity(type);
    } else if (event.key === 'ArrowDown') {
      onIncrementHoveredCity(type);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      let selectedCity: ISearchCity = null;

      if (hoveredCitiesIndex[type] < 0) {
        return;
      }

      if (suggestedCities[type] !== null) {
        if (hoveredCitiesIndex[type] >= suggestedCities[type].length) {
          return;
        }

        selectedCity = suggestedCities[type][hoveredCitiesIndex[type]];
      } else if (popularCities[type] !== null) {
        if (hoveredCitiesIndex[type] >= popularCities[type].length) {
          return;
        }

        selectedCity = popularCities[type][hoveredCitiesIndex[type]];
      }

      if (!selectedCity) {
        return;
      }

      onSelectCity(type, selectedCity);

      setTimeout(() => focusNextUnfilledInput(type), 200);
    }
  }

  function onSelectCity(
    type: ESearchPanel.ORIGIN_CITY | ESearchPanel.DESTINATION_CITY,
    city: ISearchCity,
  ) {
    if (type === ESearchPanel.DESTINATION_CITY) {
      searchContext.setDestination(city.name, city.id);
    } else {
      searchContext.setOrigin(city.name, city.id);
    }
  }

  /**
   * @description Function to be called when modifying the origin city input (it will update the suggestions of cities).
   * @param {string} cityName The name of the city.
   * @return {Promise<void>}
   */
  async function onOriginCityChange(cityName: string): Promise<void> {
    searchContext.setOrigin(cityName, null);

    if (cityName.length > 0) {
      delay(async () => {
        const cities = await searchContext.getSuggestedCities(cityName);

        setSuggestedCities((suggestedCities) => ({
          ...suggestedCities,
          [ESearchPanel.ORIGIN_CITY]: cities.filter(
            (city) => city.id !== searchContext.searchParameters.destinationId,
          ),
        }));
      }, 200);
    } else {
      setSuggestedCities((suggestedCities) => ({
        ...suggestedCities,
        [ESearchPanel.ORIGIN_CITY]: null,
      }));
      cancel();
    }
  }

  /**
   * @description Function to be called when modifying the destination city input (it will update the suggestions of cities).
   * @param {string} cityName The name of the city.
   * @return {Promise<void>}
   */
  async function onDestinationCityChange(cityName: string): Promise<void> {
    searchContext.setDestination(cityName, null);

    if (cityName.length > 0) {
      delay(async () => {
        const cities = await searchContext.getSuggestedCities(
          cityName,
          partnerId,
        );

        setSuggestedCities((suggestedCities) => ({
          ...suggestedCities,
          [ESearchPanel.DESTINATION_CITY]: cities.filter(
            (city) => city.id !== searchContext.searchParameters.originId,
          ),
        }));
      }, 200);
    } else {
      setSuggestedCities((suggestedCities) => ({
        ...suggestedCities,
        [ESearchPanel.DESTINATION_CITY]: null,
      }));
      cancel();
    }
  }

  /**
   * @description Function to be called when the origin city input is focused (it will update the suggestions of popular cities).
   * @param { React.FocusEvent<HTMLInputElement>} event
   * @return {Promise<void>}
   */
  async function onOriginCityFocus(
    event: React.FocusEvent<HTMLInputElement>,
  ): Promise<void> {
    event.target.select();
    openPanel(ESearchPanel.ORIGIN_CITY);

    if (suggestedCities[ESearchPanel.ORIGIN_CITY]) {
      return;
    }

    let cities: ISearchCity[] = [];

    if (searchContext.searchParameters.destinationId) {
      cities = await searchContext.getPopularCitiesTo(
        searchContext.searchParameters.destination.toLowerCase(),
      );
    } else {
      cities = await searchContext.getPopularCities();
    }

    setPopulaCities((suggestedCities) => ({
      ...suggestedCities,
      [ESearchPanel.ORIGIN_CITY]: cities.filter(
        (city) => city.id !== searchContext.searchParameters.destinationId,
      ),
    }));
  }

  /**
   * @description Function to be called when the destination city input is focused (it will update the suggestions of popular cities).
   * @param {React.FocusEvent<HTMLInputElement>} event
   * @return {Promise<void>}
   */
  async function onDestinationCityFocus(
    event: React.FocusEvent<HTMLInputElement>,
  ): Promise<void> {
    event.target.select();
    openPanel(ESearchPanel.DESTINATION_CITY);

    if (suggestedCities[ESearchPanel.DESTINATION_CITY]) {
      return;
    }

    let cities: ISearchCity[] = [];

    if (partnerId) {
      cities = await searchContext.getSuggestedCities('', partnerId);
    } else if (searchContext.searchParameters.originId) {
      cities = await searchContext.getPopularCitiesFrom(
        searchContext.searchParameters.origin.toLowerCase(),
      );
    } else {
      cities = await searchContext.getPopularCities();
    }

    setPopulaCities((suggestedCities) => ({
      ...suggestedCities,
      [ESearchPanel.DESTINATION_CITY]: cities.filter(
        (city) => city.id !== searchContext.searchParameters.originId,
      ),
    }));
  }

  /**
   * @description Function to be called when the destination city input is unFocused (it will delete the value entered in the input if no city has been selected).
   * @return {void}
   */
  function onDestinationCityBlur(): void {
    if (searchContext.searchParameters.destinationId) {
      return;
    }

    searchContext.setDestination('', null);
  }

  /**
   * @description Function to be called when the origin city input is unFocused (it will delete the value entered in the input if no city has been selected).
   * @return {void}
   */
  function onOriginCityBlur(): void {
    if (searchContext.searchParameters.originId) {
      return;
    }

    searchContext.setOrigin('', null);
  }

  /**
   * @description Function called to open a specific panel.
   * @param {ESearchPanel} panel The panel to be open.
   * @returns {void}
   */
  function openPanel(panel: ESearchPanel): void {
    setDisplayedPanel(panel);
    onPanelChange?.(true, panel);
  }

  /**
   * @description Function called to close panel.
   * @returns {void}
   */
  const closePanel = () => {
    setDisplayedPanel(null);
    onPanelChange?.(false, null);
  };

  /**
   * @description Will focus on next input not yet filled in.
   * @param {ESearchPanel} except Input that must not be focused
   * @returns {void}
   */
  function focusNextUnfilledInput(except?: ESearchPanel): void {
    if (windowSize.smallSize()) {
      focusInput(null);
      return;
    }

    if (
      except !== ESearchPanel.ORIGIN_CITY &&
      !searchContext.searchParameters.originId
    ) {
      focusInput(ESearchPanel.ORIGIN_CITY);
    } else if (
      except !== ESearchPanel.DESTINATION_CITY &&
      !searchContext.searchParameters.destinationId
    ) {
      focusInput(ESearchPanel.DESTINATION_CITY);
    } else if (
      except !== ESearchPanel.DEPARTURE_DATE &&
      !searchContext.searchParameters.outboundDate
    ) {
      focusInput(ESearchPanel.DEPARTURE_DATE);
    } else if (except !== ESearchPanel.DEPARTURE_DATE) {
      focusInput(ESearchPanel.DEPARTURE_DATE);
    }
  }

  function focusInput(input: ESearchPanel | null) {
    switch (input) {
      case ESearchPanel.ORIGIN_CITY:
        originCityRef.current.focus();
        break;
      case ESearchPanel.DESTINATION_CITY:
        destinationCityRef.current.focus();
        break;
      case ESearchPanel.DEPARTURE_DATE:
        departureDateRef.current.focus();
        break;
      case ESearchPanel.RETURN_DATE:
        returnDateRef.current.focus();
        break;
      case ESearchPanel.PASSENGERS:
        passengersRef.current.focus();
        break;
      default:
        closePanelButtonRef.current?.click();
        break;
    }
  }

  /**
   * @description Checks that the inputs are valid and redirects to the results page with the corresponding parameters.
   * @returns {void}
   */
  function onSubmit(): void {
    if (!validatePassengersAge(searchContext.searchParameters.passengers)) {
      openPanel(ESearchPanel.PASSENGERS);

      setTimeout(() => {
        passengersFormRef.current?.reportValidity();
      }, 100);
      return;
    }

    const urlParameters = searchContext.createSearchUrl({
      ...searchContext.searchParameters,
      refererId: referer,
    });

    if (!urlParameters) {
      return;
    }

    if (
      isMobileApp(window.navigator.userAgent) ||
      partner ||
      windowSize.smallSize()
    ) {
      history.push(`${NAVIGATION.RESULTS}${urlParameters}`);
    } else {
      /**
       * Open Booking.com affiliation page every 7 days.
       */
      const localStorageBookingKey = 'booking-affiliation-open';
      const item = localStorage.getItem(localStorageBookingKey);

      if (
        !item ||
        !dayjs(item).isValid() ||
        dayjs().diff(dayjs(item), 'day') >= 7
      ) {
        localStorage.setItem(localStorageBookingKey, dayjs().format());
        window.open(`${NAVIGATION.RESULTS}${urlParameters}`, '_blank');

        const redirectionUrl = createStay22Url({
          campaign: 'search_bar',
          address: searchContext.searchParameters.destination,
          checkin: searchContext.searchParameters.outboundDate,
          checkout: searchContext.searchParameters.returnDate,
        });

        window.location.href = redirectionUrl;
      } else {
        history.push(`${NAVIGATION.RESULTS}${urlParameters}`);
      }
    }
  }

  return (
    <Container ref={containerRef} onClick={(event) => event.stopPropagation()}>
      <SearchBar
        departureDateRef={departureDateRef}
        destinationCityRef={destinationCityRef}
        displayedPanel={displayedPanel}
        formRef={formRef}
        onDepartureDateFocus={() => openPanel(ESearchPanel.DEPARTURE_DATE)}
        onDestinationCityBlur={onDestinationCityBlur}
        onDestinationCityChange={onDestinationCityChange}
        onDestinationCityFocus={onDestinationCityFocus}
        onDestinationCityKeyDown={(
          event: React.KeyboardEvent<HTMLInputElement>,
        ) => onCitiesInputKeyDown(event, ESearchPanel.DESTINATION_CITY)}
        onOriginCityBlur={onOriginCityBlur}
        onOriginCityChange={onOriginCityChange}
        onOriginCityFocus={onOriginCityFocus}
        onOriginCityKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
          onCitiesInputKeyDown(event, ESearchPanel.ORIGIN_CITY)
        }
        onPassengersFocus={() => openPanel(ESearchPanel.PASSENGERS)}
        onRemoveReturnDate={() => searchContext.setReturnDate(null, null)}
        onReturnDateFocus={() => openPanel(ESearchPanel.RETURN_DATE)}
        onReverseCities={searchContext.reverseCities}
        onSubmit={onSubmit}
        originCityRef={originCityRef}
        passengersRef={passengersRef}
        returnDateRef={returnDateRef}
        searchParameters={searchContext.searchParameters}
      />

      <SearchPanel
        closePanelButtonRef={closePanelButtonRef}
        customStyle={panelStyle}
        displayedPanel={displayedPanel}
        focusInput={focusInput}
        getDiscountCards={searchContext.getDiscountCards}
        hoveredCitiesIndex={hoveredCitiesIndex}
        onAddPassenger={
          searchContext.passengersManagement.addPassengerByCategory
        }
        onClose={closePanel}
        onDestinationCityChange={onDestinationCityChange}
        onOriginCityChange={onOriginCityChange}
        onRemovePassenger={searchContext.passengersManagement.removePassenger}
        onSelectDestinationCity={(city: ISearchCity) => {
          onSelectCity(ESearchPanel.DESTINATION_CITY, city);

          setTimeout(
            () => focusNextUnfilledInput(ESearchPanel.DESTINATION_CITY),
            FOCUS_NEXT_INPUT_DELAY,
          );
        }}
        onSelectOriginCity={(city: ISearchCity) => {
          onSelectCity(ESearchPanel.ORIGIN_CITY, city);

          setTimeout(
            () => focusNextUnfilledInput(ESearchPanel.ORIGIN_CITY),
            FOCUS_NEXT_INPUT_DELAY,
          );
        }}
        onUpdatePassenger={searchContext.passengersManagement.updatePassenger}
        passengersFormRef={passengersFormRef}
        popularCities={popularCities}
        searchParameters={searchContext.searchParameters}
        setOutboundDate={searchContext.setOutboundDate}
        setPassengers={searchContext.setPassengers}
        setReturnDate={searchContext.setReturnDate}
        suggestedCities={suggestedCities}
      />
    </Container>
  );
};

export default TravelSearchBar;
